import React, {useEffect, useState} from "react";
import {Button, Tag} from "antd";
import {RefreshCcw} from "lucide-react";
import {useDispatch, useSelector} from "react-redux";
import {useNavigate} from "react-router-dom";
import {AdminLayout} from "../../components/layout/AdminLayout";
import {DataTable} from "../../components/table/DataTable";
import {AppDispatch, RootState} from "../../redux/store";
import {getPaymentLinkRefundRequests} from "../../redux/slice/paymentLinkSlice";
import {HeroHeader, Card, formatNaira, COLORS} from "../../designSystem";

const money = (value: any, currency = "NGN") =>
  !currency || currency === "NGN" ? formatNaira(value) : `${currency} ${Number(value || 0).toLocaleString()}`;

function PaymentLinkRefunds() {
  const dispatch: AppDispatch = useDispatch();
  const navigate = useNavigate();
  const {token} = useSelector((state: RootState) => state.account);
  const {refundRequests, pageInfo, loading} = useSelector((state: RootState) => state.paymentLinks);
  const [searchParam, setSearchParam] = useState("");
  const [groupBy, setGroupBy] = useState<string | undefined>();

  const load = (pageNumber = 1) => {
    if (token) {
      dispatch(getPaymentLinkRefundRequests({token, searchParam, pageNumber}));
    }
  };

  useEffect(() => {
    load(1);
  }, [token, searchParam]);

  return (
    <AdminLayout>
      <main className="h-full overflow-y-auto px-6 py-8 md:px-8" style={{background: COLORS.surface}}>
        <HeroHeader
          icon={RefreshCcw}
          title="Payment Link Refunds"
          subtitle="Review refund and reversal requests before funds leave PayHive."
        />

        <Card className="mt-8 p-5">
          <DataTable
            loading={loading}
            data={refundRequests}
            pageInfo={pageInfo}
            handlePageChange={load}
            searchValue={searchParam}
            onSearchChange={setSearchParam}
            searchPlaceholder="Search by creator, reference or account"
            historyKey="payment-link-refunds"
            grouping={{
              value: groupBy,
              onChange: setGroupBy,
              options: [
                {key: "status", label: "Status", get: (row: any) => row.statusText || row.status},
                {key: "creatorEmail", label: "Creator", get: (row: any) => row.creatorEmail},
                {key: "bankName", label: "Bank", get: (row: any) => row.bankName},
              ],
            }}
            totals={[{label: "Refunds on this page", dataIndex: "amount", format: formatNaira}]}
            exportFileName="payment-link-refunds"
            emptyText="No refund requests match this search."
            columns={[
              {title: "Creator", dataIndex: "creatorEmail", key: "creatorEmail"},
              {title: "Amount", key: "amount", render: (_: any, row: any) => <span className="font-mono">{money(row.amount, row.currency)}</span>},
              {title: "Bank", dataIndex: "bankName", key: "bankName"},
              {title: "Account", key: "account", render: (_: any, row: any) => `${row.accountName || ""} ${row.accountNumber || ""}`},
              {title: "Status", dataIndex: "status", key: "status", render: (value: any) => <Tag>{String(value)}</Tag>},
              {title: "Created", dataIndex: "dateCreated", key: "dateCreated", render: (value: string) => value ? new Date(value).toLocaleString() : ""},
              {title: "Action", key: "action", render: (_: any, row: any) => <Button onClick={() => navigate(`/payment-link-refunds/${row.id}`)}>Review</Button>},
            ]}
          />
        </Card>
      </main>
    </AdminLayout>
  );
}

export default PaymentLinkRefunds;
